import { FileSignature, MessageSquare, PenLine, Eye } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import ProTip from '../ProTip'

export default function ContractReviewsSection() {
  return (
    <>
      <p className="text-sm text-muted-foreground mb-3">
        Contract review used to happen entirely in email: a contract goes over for review, comes
        back redlined, goes over again, and after however many rounds it takes somebody signs it.
        Contract reviews put that on a record, so you can see what was agreed in which round and
        which contracts are sitting waiting on whom.
      </p>

      <section id="cr-raising" aria-labelledby="cr-raising-heading">
        <div className="flex items-center gap-2 mb-2">
          <FileSignature className="h-4 w-4" aria-hidden="true" />
          <h3 id="cr-raising-heading" className="font-semibold">
            Raising one
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          From <span className="font-medium">Sales &rarr; Contract reviews</span>, press{' '}
          <span className="font-medium">Send for review</span>. You need the customer, who should
          review it, and ideally the draft itself. The reviewer is told straight away, and you land
          on the review, where you can paste a link or upload the file.
        </p>
        <p className="text-sm text-muted-foreground">
          A contract often starts on paper rather than as a link, so each document can be either.
          Upload a scan as a PDF, a Word file or a photo, or paste a link, when you raise the review
          or at any point afterwards. Uploaded files are visible only to the people on the review,
          which is why they are not in the general Repository with everything else.
        </p>
        <p className="text-sm text-muted-foreground">
          Pasted links must point at Google Drive, Google Docs or DocuSign. That is deliberate: the
          link is shown inside the CRM and people click it because the CRM showed it to them, so it
          is not somewhere an arbitrary address should be able to send them.
        </p>
      </section>
      <Separator />

      <section id="cr-rounds" aria-labelledby="cr-rounds-heading">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="h-4 w-4" aria-hidden="true" />
          <h3 id="cr-rounds-heading" className="font-semibold">
            Going back and forth
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          The status always says whose turn it is, and the review moves between two of them for as
          many rounds as the contract needs:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Changes proposed</span>: the reviewer has redlined it and
            it is back with whoever raised it.
          </li>
          <li>
            <span className="font-medium">In review</span>: it is with the reviewer.
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          When you move a review on, the box asks what you are proposing, and what you type becomes
          the redline on the thread. The thread in order is the negotiation, so a question like
          &ldquo;what did we settle in round two&rdquo; has an answer later.
        </p>
        <ProTip>
          Each review counts <span className="font-medium">how many rounds</span> it has taken,
          shown on the review itself. It is counted from the record rather than tracked by anyone,
          so if a customer routinely takes five rounds, that is now visible rather than folklore.
        </ProTip>
      </section>
      <Separator />

      <section id="cr-signing" aria-labelledby="cr-signing-heading">
        <div className="flex items-center gap-2 mb-2">
          <PenLine className="h-4 w-4" aria-hidden="true" />
          <h3 id="cr-signing-heading" className="font-semibold">
            Agreeing and signing
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Once the terms are settled the review goes to <span className="font-medium">Agreed</span>.
          Add the link to the final document, usually DocuSign, and it becomes{' '}
          <span className="font-medium">Awaiting signature</span>. A review cannot reach that state
          without a document link, because there would be nothing to sign.
        </p>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium">
            Only the reviewer the contract was sent to can sign it.
          </span>{' '}
          Administrators can correct anything else, including putting a review back a step, but not
          this. Signing is a legal act and should name one person.
        </p>
        <ProTip>
          <span className="font-medium">A signed review cannot be reopened.</span> Nor can a
          declined or withdrawn one. If terms come apart before anything is signed, move it back to{' '}
          <span className="font-medium">In review</span> rather than withdrawing and starting again,
          which would lose the history.
        </ProTip>
      </section>
      <Separator />

      <section id="cr-visibility" aria-labelledby="cr-visibility-heading">
        <div className="flex items-center gap-2 mb-2">
          <Eye className="h-4 w-4" aria-hidden="true" />
          <h3 id="cr-visibility-heading" className="font-semibold">
            Who can see a review
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Everyone with access to contract reviews can see what is in flight: the title, the
          customer, the status, whose turn it is and how many rounds it has taken. Knowing that a
          renewal is stuck in its fourth round is useful to the whole team, not just the two people
          in it. Because the title is that widely visible, keep pricing and negotiating positions
          out of it.
        </p>
        <p className="text-sm text-muted-foreground">
          What stays private is the contract itself. The documents, the links and the thread of
          redlines are visible only to the person who raised the review, the reviewer, and
          administrators. Open a review you are not part of and you will see how far it has got, and
          a line telling you who can see the terms.
        </p>
        <p className="text-sm text-muted-foreground">
          The queue shows the whole team&rsquo;s by default. Tick{' '}
          <span className="font-medium">Only mine</span> for the ones you raised or are reviewing.
        </p>
      </section>
    </>
  )
}
